import { apiService } from '.';
import {
    Pqr,
    PqrFilter,
    PqrFilterWeb,
    PqrSmartHistory,
    PqrStatusHistory,
    PqrSurvey,
    PqrHistorical,
    PqrAlertHistory,
    PqrMailHistory,
    PqrNotificationAreasHistory,
} from '../shared/types/modules/pqr';
import { SamplesFilter } from '../shared/types/legalProceedings/samples';
import { SmartSupervisionEnum } from '../shared/types/shared';
import { asyncUpdateTemplate } from './template.service';

export const asyncGetPqrs = async (
    filters: PqrFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get('/api/pqr', {
            limit: pageSize || 20,
            page: page || 0,
            ...filters,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqrDataHistory = async (
    filters: PqrFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get('/api/pqr/history', {
            limit: pageSize || 20,
            page: page || 0,
            ...filters,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetSamplesPqr = async (
    filters: SamplesFilter,
    page?: number,
    pageSize?: number
): Promise<{ items: Pqr[]; length: number }> => {
    try {
        const { caseId, percentage, ...rest } = filters || {};
        const hasPercentage = Boolean(
            percentage !== undefined &&
                percentage !== null &&
                String(percentage).trim() !== ''
        );

        const {
            items: data,
        }: {
            items: { items: Pqr[]; length: number };
        } = await apiService.get(
            hasPercentage ? '/api/pqr/samples' : '/api/pqr/samples/list',
            {
                limit: pageSize || 20,
                page: page || 0,
                ...rest,
                ...(caseId ? { callId: caseId } : {}),
                ...(hasPercentage ? { percentage } : {}),
            }
        );

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqr = async (idPqr: string): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.get(`/api/pqr/${idPqr}`);

        return data;
    } catch (error) {}
};

export const asyncGetPqrSurvey = async (
    idPqr: string
): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.get(`/api/pqr/survey/${idPqr}`);
        return data;
    } catch (error) {}
};

export const asyncCreatePqr = async (pqr: Pqr): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.post('/api/pqr', pqr);

        return data;
    } catch (error) {}
};

export const asyncCreatePqrWeb = async (pqr: Pqr): Promise<Pqr | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.post('/api/pqr/pqrweb', pqr);

        return data;
    } catch (error) {}
};

export const asyncUpdatePqr = async (
    id: string,
    pqr: Pqr
): Promise<Pqr | undefined> => {
    try {
        if (
            pqr.statusManagement?.sendEmailExpansionInformation ||
            pqr.statusManagement?.sendEmailExtension ||
            pqr.statusManagement?.sendEmailResponse
        ) {
            await asyncUpdateTemplate({
                id: pqr.statusManagement?.templateId,
                body: pqr.statusManagement?.template,
                code: pqr.statusManagement?.templateType,
            });
        }
        const {
            items: data,
        }: {
            items: Pqr;
        } = await apiService.patch(`/api/pqr/${id}`, pqr);

        return data;
    } catch (error) {}
};

export const asyncGetPqrSmartHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number,
    type?: SmartSupervisionEnum[]
): Promise<{
    items: PqrSmartHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrSmartHistory[]; length: number };
        } = await apiService.get('/api/pqr/smart-history', {
            pqrId,
            limit: pageSize || 20,
            page: page || 0,
            type: type?.length ? type : undefined,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncFindPublicPqr = async (filters: {
    consecutiveFormat: string;
    document: string;
    typeDocument: string;
}): Promise<PqrFilterWeb | undefined> => {
    try {
        // Realiza la solicitud al endpoint correspondiente
        const { data }: { data: PqrFilterWeb } = await apiService.get(
            '/api/pqr/pqrwebinfo',
            {
                consecutiveFormat: filters.consecutiveFormat,
                document: filters.document,
                typeDocument: filters.typeDocument,
            }
        );

        return data;
    } catch (error) {
        console.error('Error en asyncFindPublicPqr:', error);
        return undefined;
    }
};

export const asyncGetPqrStatusHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrStatusHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrStatusHistory[]; length: number };
        } = await apiService.get(`/api/pqr/status-history/${pqrId}`, {
            limit: pageSize || 20,
            page: page || 0,
        });

        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqrAlertHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrAlertHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrAlertHistory[]; length: number };
        } = await apiService.get(`/api/pqr/alert-history/${pqrId}`, {
            limit: pageSize || 20,
            page: page || 0,
        });
        if (!data || !data.items) {
            return { items: [], length: 0 };
        }
        return { items: data.items, length: data.length };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqrMailHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrMailHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrMailHistory[]; length: number };
        } = await apiService.get(`/api/pqr/mail-history/${pqrId}`, {
            limit: pageSize || 20,
            page: page || 0,
        });

        if (!data || !data.items) {
            return { items: [], length: 0 };
        }

        const transformedItems: PqrMailHistory[] = data.items.map(
            (item: any) => ({
                id: item.id,
                subject: item.data?.Subject || null,
                status: item.response?.statusCode || null,
                sent_at: new Date(item.created_at),
                created_at: new Date(item.created_at),
                to: (item.data?.Recipients || [])
                    .filter(
                        (r: { isCc: any; isCco: any }) => !r.isCc && !r.isCco
                    )
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                cc: (item.data?.Recipients || [])
                    .filter((r: { isCc: boolean }) => r.isCc === true)
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                bcc: (item.data?.Recipients || [])
                    .filter((r: { isCco: boolean }) => r.isCco === true)
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                sent_by: item.username || null,
            })
        );

        return {
            items: transformedItems,
            length: transformedItems.length,
        };
    } catch (error) {
        return { items: [], length: 0 };
    }
};
export const asyncGetPqrMailHistoryWithArea = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrMailHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrMailHistory[]; length: number };
        } = await apiService.get(`/api/pqr/mail-history-areas/${pqrId}`, {
            limit: pageSize || 20,
            page: page || 0,
        });

        if (!data || !data.items) {
            return { items: [], length: 0 };
        }

        const transformedItems: PqrMailHistory[] = data.items.map(
            (item: any) => ({
                id: item.id,
                subject: item.data?.Subject || null,
                status: item.response?.statusCode || null,
                sent_at: new Date(item.created_at),
                created_at: new Date(item.created_at),
                to: (item.data?.Recipients || [])
                    .filter(
                        (r: { isCc: any; isCco: any }) => !r.isCc && !r.isCco
                    )
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                cc: (item.data?.Recipients || [])
                    .filter((r: { isCc: boolean }) => r.isCc === true)
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                bcc: (item.data?.Recipients || [])
                    .filter((r: { isCco: boolean }) => r.isCco === true)
                    .map((r: { To: any }) => r.To)
                    .join(';'),
                sent_by: item.username || null,
            })
        );

        return {
            items: transformedItems,
            length: transformedItems.length,
        };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetPqrNotificationAreasHistory = async (
    pqrId: string,
    page?: number,
    pageSize?: number
): Promise<{
    items: PqrNotificationAreasHistory[];
    length: number;
}> => {
    try {
        const {
            items: data,
        }: {
            items: { items: PqrNotificationAreasHistory[]; length: number };
        } = await apiService.get(
            `/api/pqr/notification-areas-history/${pqrId}`,
            {
                limit: pageSize || 20,
                page: page || 0,
            }
        );

        const filteredItems = data.items.filter(
            (item) =>
                item.changes &&
                Object.prototype.hasOwnProperty.call(
                    item.changes,
                    'emailSent'
                ) &&
                Array.isArray(item.changes.emailSent) &&
                item.changes.emailSent.length > 0
        );

        const transformedItems = filteredItems.flatMap((item) => {
            const emailSentArray = item.changes?.emailSent;

            const filteredEmailSent = emailSentArray.filter(
                (emailElem: any) =>
                    emailElem?.template?.code === 'PQR_REDIRECT' ||
                    emailElem?.template?.code === 'PQR_REDIRECT_REQUEST' ||
                    emailElem?.template?.code === 'PQR_REDIRECT_ADVOCATE'
            );

            return filteredEmailSent.map((emailElem: any) => {
                const recipients = (emailElem.data?.Recipients || [])
                    .filter((r: any) => !r.isCc && !r.isCco)
                    .map((r: any) => r.To)
                    .join(';');

                return {
                    id: item.id,
                    sent_at: emailElem.created_at,
                    user:
                        (emailElem.createdBy?.name || '') +
                        ' ' +
                        (emailElem.createdBy?.surname || ''),
                    recipients,
                    pqrId: item.pqrId,
                    status: item.changes?.info?.status?.name || null,
                };
            });
        });

        return {
            items: transformedItems,
            length: transformedItems.length,
        };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncRetryM2 = async (
    pqrId: string
): Promise<
    | {
          type: string;
          result: any;
      }[]
    | undefined
> => {
    try {
        const {
            items: data,
        }: {
            items: { type: string; result: any }[];
        } = await apiService.post(`/api/pqr/retry-super-m2/${pqrId}`, {});

        return data || { items: [], length: 0 };
    } catch (error) {}
};

export const asyncRetryAck = async (
    pqrId: string
): Promise<
    | {
          type: string;
          result: any;
      }[]
    | undefined
> => {
    try {
        const {
            items: data,
        }: {
            items: { type: string; result: any };
        } = await apiService.post(`/api/pqr/retry-ack/${pqrId}`, {});

        return data ? [data] : [];
    } catch (error) {}
};

export const asyncCreatePqrSurvey = async (
    pqrSurvey: PqrSurvey
): Promise<PqrSurvey | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: PqrSurvey;
        } = await apiService.post('/api/pqr/survey', pqrSurvey);

        return data;
    } catch (error) {}
};

export const asyncCanDoSurvey = async (
    id: string
): Promise<
    | {
          canDo: boolean;
          halfName: string;
      }
    | undefined
> => {
    try {
        const {
            items,
        }: {
            items: {
                canDo: boolean;
                halfName: string;
            };
        } = await apiService.get(`/api/pqr/can-do-survey/${id}`);

        return items;
    } catch (error) {}
};

export const asyncGetPqrHistorical = async (
    id: string
): Promise<PqrHistorical[] | undefined> => {
    try {
        const {
            items: data,
        }: {
            items: PqrHistorical[];
        } = await apiService.get(`/api/pqr/${id}/historical`);

        return data || [];
    } catch (error) {}
};
